import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import JsonView from "./JsonView";

/**
 * ScopesFormView - Form interface for scopes.json editing
 * 
 * Handles OAuth scope arrays: {"scopes": ["scope1", "scope2"]}
 * 
 * @param {Object} props
 * @param {string} props.value - JSON string of scopes.json content
 * @param {Function} props.onChange - Callback(jsonString: string) when form data changes
 * @param {boolean} props.readOnly - Disable editing
 * 
 * State Management:
 * - scopesList: string[] for OAuth scopes
 * 
 * Sync Logic:
 * - Parse value on mount/change -> populate form state
 * - Form changes -> build JSON -> call onChange with stringified result
 */
const ScopesFormView = ({
  value,
  onChange,
  readOnly = false,
  formState,
  onFormStateChange,
  mode = "form",
  onModeSwitch,
  supportsFormView = true,
  onError,
  fileType = "scopes"
}) => {
  const [scopeInput, setScopeInput] = useState("");

  // Use lifted state from parent (preserved across view switches)
  const scopes = formState?.scopes || [];
  
  const setScopes = (newScopes) => {
    if (onFormStateChange) {
      onFormStateChange({
        ...formState,
        scopes: newScopes
      });
    }
  };

  // Build JSON and notify parent
  const syncToParent = (scopesList) => {
    const json = { scopes: scopesList };
    const jsonString = JSON.stringify(json, null, 2);
    
    if (onChange) {
      onChange(jsonString);
    }
  };

  // Add scope
  const handleAddScope = () => {
    const trimmed = scopeInput.trim();
    if (!trimmed) return;
    
    if (scopes.includes(trimmed)) {
      return; // Silently ignore duplicates
    }
    
    const newList = [...scopes, trimmed];
    setScopes(newList);
    setScopeInput("");
    syncToParent(newList);
  };

  // Remove scope
  const handleRemoveScope = (scope) => {
    const newList = scopes.filter(s => s !== scope);
    setScopes(newList);
    syncToParent(newList);
  };

  return (
    <div className="space-y-4">
      {/* Content Section with Toggle */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-semibold text-ink-gray-700 dark:text-ink-gray-300">
            {mode === "form" ? "OAuth Scopes" : "JSON Editor"}
          </label>
          
          {/* View Mode Toggle */}
          {supportsFormView && (
            <div className="flex items-center gap-0.5 bg-fill-gray-100 dark:bg-fill-gray-800 rounded-md p-0.5">
              <button
                type="button"
                onClick={() => onModeSwitch("form")}
                disabled={mode === "form"}
                className={`px-2 py-1 text-sm font-mono rounded transition-all ${
                  mode === "form"
                    ? "bg-fill-white dark:bg-fill-gray-700 text-ink-primary shadow-sm"
                    : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-300"
                }`}
                title="Form View"
              >
                [form]
              </button>
              <button
                type="button"
                onClick={() => onModeSwitch("json")}
                disabled={mode === "json"}
                className={`px-2 py-1 text-sm font-mono rounded transition-all ${
                  mode === "json"
                    ? "bg-fill-white dark:bg-fill-gray-700 text-ink-primary shadow-sm"
                    : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-300"
                }`}
                title="JSON View"
              >
                {"{json}"}
              </button>
            </div>
          )}
        </div>

        {/* Conditional Content: Form or JSON */}
        {mode === "json" ? (
          <JsonView
            value={value}
            onChange={onChange}
            onError={onError}
            fileType={fileType}
            readOnly={readOnly}
          />
        ) : (
          <div className="space-y-3">
            {/* Add Scope Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={scopeInput}
                onChange={(e) => setScopeInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddScope()}
                placeholder="e.g., https://www.googleapis.com/auth/userinfo.email"
                disabled={readOnly}
                className="photon-field flex-1 px-4 py-2 border border-edge-gray-300 dark:border-edge-gray-600 rounded-lg bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-white focus:ring-2 focus:ring-focus-primary focus:border-transparent text-sm"
              />
              <button
                type="button"
                onClick={handleAddScope}
                disabled={!scopeInput.trim() || readOnly}
                className="px-4 py-2 bg-fill-primary text-ink-on-primary rounded-lg hover:bg-fill-primary-hover-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
              Add OAuth scopes for this provider
            </p>

            {/* Scopes List */}
            {scopes.length > 0 && (
              <div className="space-y-2">
                {scopes.map((scope, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 bg-fill-gray-50 dark:bg-fill-deep-surface border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg"
                  >
                    <span className="text-sm text-ink-gray-700 dark:text-ink-gray-300 font-mono break-all">
                      {scope}
                    </span>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleRemoveScope(scope)}
                        className="text-ink-red-500 hover:text-ink-red-700 dark:hover:text-ink-red-400 flex-shrink-0"
                        title="Remove scope"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {scopes.length === 0 && (
              <div className="text-center py-8 text-ink-gray-500 dark:text-ink-gray-400 text-sm">
                No scopes added yet. Add your first OAuth scope above.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScopesFormView;
